/* Maerel London — add to bag (AJAX) + live quantity steppers
 *
 * The product cards and the product page use Shopify's native
 * {% form 'product' %} tag, which posts to /cart/add and makes the browser
 * leave the page for /cart. That's a jarring way to add a gift box. This
 * intercepts those submits and adds via /cart/add.js instead, so the shopper
 * stays put: the bag count ticks up, a small toast slides in, and the card's
 * button turns into a quantity stepper.
 *
 * Once a box is in the bag, its card shows a "− qty +" stepper (markup lives in
 * maerel-product-card.liquid under [data-maerel-buy]). The stepper edits the
 * live cart line — which of Shopify's three cart routes it uses depends on the
 * direction and on what the analytics pixel hears, see flushChange — so
 * shoppers can change how many they're sending without leaving the page. On
 * every page load we read /cart.js and paint each card to match what's actually
 * in the bag, so the state survives navigation and reloads — like a regular shop.
 *
 * Everything is delegated from the document, so cards rendered after load are
 * handled too. Adds fall back to a normal form submit if the request fails, so
 * adding to the bag always works.
 *
 * The toast announces itself: `maerel:cart:toast` on document, detail
 * `{ open: true|false }` — the product page's sticky buy bar hides while it is
 * open, since both sit at the foot of a phone screen.
 */
(function () {
  var ROUTES = (window.Theme && Theme.routes) || {};
  var ADD_URL = ROUTES.cart_add_url || '/cart/add.js';
  var CHANGE_URL = (ROUTES.cart_change_url || '/cart/change') + '.js';
  var UPDATE_URL = (ROUTES.cart_update_url || '/cart/update') + '.js';
  var CART_URL = ROUTES.cart_url || '/cart';
  var CART_JS_URL = CART_URL + '.js';
  var MAX_QTY = 9;

  // ---- Add to bag (form interception) -------------------------------------

  function isAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    return /\/cart\/add(\.js)?(\?|$)/.test(form.getAttribute('action') || '');
  }

  // Capture phase, and the event stops here. Cancelling the submit is not
  // enough: a cancelled submit still reaches the listeners Shopify's web pixel
  // puts on the document, and Shopify reads the form to announce an add of its
  // own — so one click sent *two* add_to_cart events to GA4 and Google Ads, the
  // form-read one carrying no product and no value on any page without a single
  // product in context (the homepage grid, every card). Taking the event before
  // it propagates leaves exactly one add, announced from the /cart/add.js
  // response with the whole line on it. The native fallback below is
  // unaffected — form.submit() fires no submit event.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!isAddForm(form) || form.dataset.maerelSubmitting) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    addToBag(form);
  }, true);

  function addToBag(form) {
    var btn = form.querySelector('[type="submit"]');
    if (btn && btn.disabled) return;
    form.dataset.maerelSubmitting = '1';

    var card = form.closest('[data-maerel-buy]');
    var label = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-adding');
      btn.innerHTML = 'Adding…';
    }

    fetch(ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error((body && body.description) || 'Could not add to bag');
          return body;
        });
      })
      .then(function (item) {
        delete form.dataset.maerelSubmitting;
        if (card) {
          // The button is about to be replaced by the stepper, so just reset it.
          resetButton(btn, label);
        } else if (btn) {
          // PDP / other forms with no card stepper: confirm on the button itself.
          btn.classList.remove('is-adding');
          btn.classList.add('is-added');
          btn.innerHTML = 'Added <span class="arr">✓</span>';
          window.setTimeout(function () {
            btn.classList.remove('is-added');
            resetButton(btn, label);
          }, 1600);
        }
        refreshCart(true);
        showToast(item);
      })
      .catch(function () {
        // Network/validation trouble — let the native form take over so the
        // shopper can still complete the add the standard way.
        delete form.dataset.maerelSubmitting;
        resetButton(btn, label);
        form.submit();
      });
  }

  function resetButton(btn, label) {
    if (!btn) return;
    btn.classList.remove('is-adding');
    btn.disabled = false;
    btn.innerHTML = label;
  }

  // ---- Quantity steppers --------------------------------------------------

  // Rapid clicks are coalesced. We hold the desired quantity in JS state on the
  // card (never read it back from the DOM — a slow in-flight response could
  // clobber it), update the display optimistically, then debounce a single
  // request carrying the final quantity. applyCart skips any card mid-change,
  // so a stale response can't fight the optimistic count.

  document.addEventListener('click', function (e) {
    var step = e.target.closest('[data-step-up], [data-step-down]');
    if (!step) return;
    var card = step.closest('[data-maerel-buy]');
    if (!card) return;
    e.preventDefault();

    var base = card._desired != null ? card._desired : readQty(card);
    var next = base + (step.hasAttribute('data-step-up') ? 1 : -1);
    if (next > MAX_QTY || next < 0) return;

    card._desired = next;
    if (next >= 1) setStepVal(card, next);
    else toggleStepper(card, false); // dropping to 0 — show the add button (keep lineKey)
    scheduleChange(card);
  });

  function readQty(card) {
    var valEl = card.querySelector('[data-step-val]');
    return parseInt(valEl && valEl.textContent, 10) || 0;
  }

  function setStepVal(card, qty) {
    var valEl = card.querySelector('[data-step-val]');
    var up = card.querySelector('[data-step-up]');
    if (valEl) valEl.textContent = qty + ' in bag';
    if (up) up.disabled = qty >= MAX_QTY;
  }

  function toggleStepper(card, show) {
    var form = card.querySelector('.pcard-add-form');
    var step = card.querySelector('[data-maerel-stepper]');
    if (form) form.hidden = show;
    if (step) step.hidden = !show;
  }

  function scheduleChange(card) {
    window.clearTimeout(card._timer);
    card._timer = window.setTimeout(function () { flushChange(card); }, 250);
  }

  function flushChange(card) {
    if (card._busy) return; // a request is in flight; it re-flushes on return
    var key = card.dataset.lineKey;
    if (!key || card._desired == null) { card._desired = null; refreshCart(false); return; }
    var target = card._desired;
    var have = card._have || 0;
    card._busy = true;

    // Up adds; down either empties the line or updates it. The three cart
    // routes are not interchangeable, and each is here for one reason:
    //
    //   /cart/add.js     the only route Shopify's pixel reads as an add, so a
    //                    box added from a card reaches GA4 and Google Ads like
    //                    one added from the button — and the only one that will
    //                    carry a line past the stock Shopify has counted, which
    //                    is why the + used to snap back on every box the shop
    //                    was selling past its count.
    //   /cart/change.js  read as a removal, so the last box leaving the bag is
    //                    reported. It refuses any quantity above that counted
    //                    stock and clamps the line on its way out, so it is
    //                    trusted with 0 and nothing else.
    //   /cart/update.js  always obeyed, never heard: every other step down.
    //                    Going down through change.js and repairing the clamp
    //                    afterwards would read to the pixel as an *add*, and a
    //                    silent decrease beats a wrong one.
    var req;
    if (target > have) {
      req = post(ADD_URL, { id: card.getAttribute('data-variant-id'), quantity: target - have });
    } else if (target === 0) {
      req = post(CHANGE_URL, { id: key, quantity: 0 });
    } else {
      var updates = {};
      updates[key] = target;
      req = post(UPDATE_URL, { updates: updates });
    }

    req
      .then(function (body) {
        card._busy = false;
        announceQuantity(card, have, target);
        if (card._desired !== target) { flushChange(card); return; } // user kept clicking
        card._desired = null; // settled — server state is authoritative again
        // change.js and update.js answer with the whole cart; add.js with the
        // line it added.
        if (body && body.items) applyCart(body, true);
        else refreshCart(true);
      })
      .catch(function () {
        card._busy = false;
        card._desired = null;
        refreshCart(false);
      });
  }

  // A card stepper changes the same bag the cart page does, so it says so the
  // same way: maerel-analytics.js listens and reports cart_quantity_change,
  // which is the only place a quantity merely falling is ever recorded.
  function announceQuantity(card, from, to) {
    try {
      var article = card.closest('[data-analytics-item]');
      var item = article ? JSON.parse(article.getAttribute('data-analytics-item')) : {};
      document.dispatchEvent(new CustomEvent('maerel:cart:quantity', {
        detail: {
          handle: article ? article.getAttribute('data-analytics-handle') : null,
          title: item.name,
          variant_id: card.getAttribute('data-variant-id'),
          price: item.price,
          from: from,
          to: to
        }
      }));
    } catch (e) { /* no CustomEvent constructor — then nothing is listening either */ }
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Could not update bag');
      return res.json();
    });
  }

  // ---- Cart state → DOM ---------------------------------------------------

  function refreshCart(bump) {
    fetch(CART_JS_URL, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { applyCart(cart, bump); })
      .catch(function () {});
  }

  function applyCart(cart, bump) {
    paintCount(cart.item_count, bump);
    var byVariant = {};
    (cart.items || []).forEach(function (it) { byVariant[String(it.variant_id)] = it; });
    document.querySelectorAll('[data-maerel-buy]').forEach(function (card) {
      // Skip cards mid-change so a stale snapshot can't fight the optimistic count.
      if (card._desired != null || card._busy) return;
      paintCard(card, byVariant[card.getAttribute('data-variant-id')]);
    });
  }

  function paintCard(card, item) {
    var form = card.querySelector('.pcard-add-form');
    var step = card.querySelector('[data-maerel-stepper]');
    if (item && item.quantity > 0) {
      card.dataset.lineKey = item.key;
      card._have = item.quantity; // what the server holds — a step reads it for its direction
      setStepVal(card, item.quantity);
      if (form) form.hidden = true;
      if (step) step.hidden = false;
    } else {
      delete card.dataset.lineKey;
      card._have = 0;
      if (form) form.hidden = false;
      if (step) step.hidden = true;
    }
  }

  function paintCount(n, bump) {
    document.querySelectorAll('.bag-badge').forEach(function (badge) {
      badge.textContent = n;
      if (n > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
      if (!bump) return;
      var ic = badge.closest('.hd-ic');
      if (!ic) return;
      ic.classList.remove('bag-bumped');
      void ic.offsetWidth; // reflow so the animation can replay on each change
      ic.classList.add('bag-bumped');
    });
  }

  // ---- Toast --------------------------------------------------------------

  var toast, toastTimer;

  function ensureToast() {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    // Live inside .maerel-root so the .btn CTA and palette colours cascade;
    // it's position:fixed, so the parent doesn't affect where it sits.
    (document.querySelector('.maerel-root') || document.body).appendChild(toast);
    return toast;
  }

  function showToast(item) {
    var el = ensureToast();
    var img = item.image || (item.featured_image && item.featured_image.url) || '';
    var title = item.product_title || item.title || 'Your item';

    el.innerHTML =
      '<button class="cart-toast-x" type="button" aria-label="Dismiss">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
      '</button>' +
      '<div class="cart-toast-row">' +
        (img ? '<img class="cart-toast-img" src="' + esc(img) + '" alt="" width="56" height="56">' : '') +
        '<div class="cart-toast-info">' +
          '<div class="cart-toast-eyebrow">Added to your bag</div>' +
          '<div class="cart-toast-title">' + esc(title) + '</div>' +
        '</div>' +
      '</div>' +
      '<a class="btn cart-toast-cta" href="' + esc(CART_URL) + '">View bag <span class="arr">&rarr;</span></a>';

    el.querySelector('.cart-toast-x').addEventListener('click', hideToast);

    // show on the next frame so the transition runs
    window.requestAnimationFrame(function () { el.classList.add('is-open'); });
    emitToast(true);

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, 5000);
  }

  function hideToast() {
    if (toast && toast.classList.contains('is-open')) {
      toast.classList.remove('is-open');
      emitToast(false);
    }
    window.clearTimeout(toastTimer);
  }

  function emitToast(open) {
    try {
      document.dispatchEvent(new CustomEvent('maerel:cart:toast', { detail: { open: open } }));
    } catch (e) { /* no CustomEvent constructor — then nothing is listening either */ }
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ---- Init ---------------------------------------------------------------

  // Paint cards from the real bag on load, so steppers reflect what's already
  // in the bag after navigation/reload. Skip the request when there's nothing
  // to paint (the header count is already server-rendered).
  function init() {
    if (document.querySelector('[data-maerel-buy]')) refreshCart(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
